/**
 =========================================================
 * Material Dashboard 2 PRO React - v2.1.0
 =========================================================

 * Product Page: https://www.creative-tim.com/product/material-dashboard-pro-react
 * Copyright 2022 Creative Tim (https://www.creative-tim.com)

 Coded by www.creative-tim.com

 =========================================================

 * The above copyright notice and this permission notice shall be included in all copies or substantial portions of the Software.
 */

import { useEffect, useState, Suspense } from "react";

// react-router components
import { Route, Routes, useLocation } from "react-router-dom";

// @mui material components
import { ThemeProvider } from "@mui/material/styles";
import CssBaseline from "@mui/material/CssBaseline";

// Material Dashboard 2 PRO React themes
import theme from "assets/theme";

// Material Dashboard 2 PRO React Dark Mode themes
import themeDark from "assets/theme-dark";

// Material Dashboard 2 PRO React routes
import routes from "routes";

// Material Dashboard 2 PRO React contexts
import { useMaterialUIController } from "context";

//Componente para las rutas de los forms
import MainLayout from "layouts/main.layout";
import NewUser from "layouts/almacen/pages/new-user/new_user";
import NewEntity from "layouts/almacen/pages/new-entidad/new_entidad";

//Componentes de seguridad
import PrivateRoutes from "config/private_routes";
import ComponentsPrivatization from "config/component_privatization";

import { GoogleOAuthProvider } from "@react-oauth/google";

//Roles y rutas
import {
  AUXILIAR_DOCENTE,
  COORDINADOR_DOCENTE,
  ESTUDIANTE,
  FIRMANTE_BOLETA,
  PROFESOR,
} from "roles";
import {
  ENTIDAD_EDIT_URL,
  ENTIDAD_NEW_URL,
  GRUPOS_LIST_URL,
  USER_EDIT_URL,
  USER_NEW_URL,
} from "config/bases_url";

import { checkPerm, toIsoDate } from "helpers/functions";
import EditCiclo from "layouts/escuela/pages/edit-ciclo/edit_ciclo";
import { DataTableGrupos } from "layouts/escuela/data-tables";
import { useLazyGetCiclosQuery } from "services";
import { useDispatch, useSelector } from "react-redux";
import { auth_state } from "slices";
import { Icon } from "@mui/material";
import { privateRoutes } from "./config/app-router/private-routes";
import { publicRoutes } from "./config/app-router/public-routes";
import MySuspense from "./config/suspense";
import {
  calificaciones,
  setCicloSeleccionado,
} from "slices/Calificaciones/calificaciones.slice";


export default function App() {
  const [controller] = useMaterialUIController();
  const { direction, darkMode } = controller;
  const { pathname } = useLocation();
  const [stateRoutes, setStateRoutes] = useState(routes);
  const { authenticated, user_info: userAuth } = useSelector(auth_state);
  const [
    fetchCiclosApi,
    { data: ciclos, isFetching: ciclosLoading, isError: ciclosError, error: ciclosErrorObj },
  ] = useLazyGetCiclosQuery();

  const dispatch = useDispatch();
  const { cicloSeleccionado } = useSelector(calificaciones);

  // Setting the dir attribute for the body element
  useEffect(() => {
    document.body.setAttribute("dir", direction);
  }, [direction]);

  // Setting page scroll to 0 when changing the route
  useEffect(() => {
    document.documentElement.scrollTop = 0;
    document.scrollingElement.scrollTop = 0;
  }, [pathname]);

  useEffect(() => {
    if (ciclos) dispatch(setCicloSeleccionado(ciclos.find((el) => el.activo)));
  }, [ciclos]);

  // ejecutar lazy query solo al autenticar
  useEffect(() => {
    let req;
    if (
      authenticated &&
      !userAuth?.is_staff &&
      !userAuth?.groups?.some((e) => e.name === ESTUDIANTE)
    ) {
      req = fetchCiclosApi();
    }
    return () => {
      req?.abort?.();
    };
  }, [authenticated]);

  useEffect(() => {
    const fetchCiclos = () => {
      if (authenticated && ciclos) {
        const rutasCiclos = ciclos?.map((el) => {
          return {
            name: el.nombre,
            key: el.id,
            type: "item",
            icon: (
              <Icon fontSize="medium">
                {el.activo ? "event_available" : "event_busy"}
              </Icon>
            ),
            ciclo: true,
            roles: [
              COORDINADOR_DOCENTE,
              AUXILIAR_DOCENTE,
              PROFESOR,
              FIRMANTE_BOLETA,
            ],
            onClick: () => {
              dispatch(setCicloSeleccionado(el));
            },
          };
        });

        setStateRoutes((prevRoutes) =>
          prevRoutes.map((route) => {
            if (route.key === "ciclos") {
              const newRoute = { ...route };
              newRoute.name = cicloSeleccionado
                ? `${toIsoDate(cicloSeleccionado.fecha_inicio, true)}-${toIsoDate(cicloSeleccionado.fecha_fin, true)}`
                : "Año Escolar";
              newRoute.collapse = [...rutasCiclos];
              return newRoute;
            }

            return route;
          })
        );
      }
    };
    if (
      !userAuth?.is_staff &&
      !userAuth?.groups.some((e) => e.name === ESTUDIANTE)
    )
      fetchCiclos();
  }, [ciclos, cicloSeleccionado]);

  const getRoutes = (allRoutes) =>
    allRoutes.map((route) => {
      if (route.collapse) {
        return getRoutes(route.collapse);
      }

      if (
        (route.route && route.for_all) ||
        (route.route && checkPerm(route.roles))
      ) {
        return (
          <Route
            exact
            path={route.route}
            element={route.component}
            key={route.key}
          />
        );
      }

      return null;
    });

  const generateInternalRoutes = (
    routes,
    transformRoute = (route) => route.element
  ) =>
    routes.map((route) => (
      <Route
        exact
        path={route.path}
        element={
          <Suspense fallback={<MySuspense />}>{transformRoute(route)}</Suspense>
        }
        key={route.path}
      />
    ));

  return (
    <GoogleOAuthProvider clientId="623355275973-iufvlv30orht76rvbdkanviajr65e564.apps.googleusercontent.com">
      <ThemeProvider theme={darkMode ? themeDark : theme}>
        <CssBaseline />
        <Suspense fallback={<MySuspense />}>
          <Routes>
            {generateInternalRoutes(publicRoutes)}
            <Route element={<PrivateRoutes />}>
              <Route element={<MainLayout routes={stateRoutes} />}>
                {getRoutes(stateRoutes)}
                {generateInternalRoutes(privateRoutes, (route) =>
                  route.permitted ? (
                    <ComponentsPrivatization
                      permitted={route.permitted}
                      redirect={true}
                    >
                      <Suspense fallback={<MySuspense />}>
                        {route.element}
                      </Suspense>
                    </ComponentsPrivatization>
                  ) : (
                    <Suspense fallback={<MySuspense />}>
                      {route.element}
                    </Suspense>
                  )
                )}

                {/* Usuarios */}
                <Route
                  exact
                  path={USER_NEW_URL}
                  element={
                    <Suspense fallback={<MySuspense />}>
                      <NewUser />
                    </Suspense>
                  }
                />
                <Route
                  exact
                  path={USER_EDIT_URL + "/:id"}
                  element={
                    <Suspense fallback={<MySuspense />}>
                      <NewUser />
                    </Suspense>
                  }
                />

                {/* Entidades */}
                <Route
                  exact
                  path={ENTIDAD_NEW_URL}
                  element={
                    <Suspense fallback={<MySuspense />}>
                      <NewEntity />
                    </Suspense>
                  }
                />
                <Route
                  exact
                  path={ENTIDAD_EDIT_URL + "/:id"}
                  element={
                    <Suspense fallback={<MySuspense />}>
                      <NewEntity />
                    </Suspense>
                  }
                />
              </Route>
            </Route>
          </Routes>
        </Suspense>
      </ThemeProvider>
    </GoogleOAuthProvider>
  );
}
